import React from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "utils";

// Loading placeholder for the Groups dashboard (OKO2-3684): the shape of a
// bundle — a filter/header bar over a grid of group cards — instead of a
// spinner, so the page doesn't jump when the real content lands. Same pulse
// the Headlines skeleton uses.

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 0.3; }
  100% { opacity: 0.6; }
`;

// Shared with the other loading placeholders (e.g. the session history list):
// one pulse container + bar so every skeleton breathes the same way.
export const SkeletonPulse = styled.div`
  animation: ${pulse} 1.5s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.5;
  }
`;

export const SkeletonBar = styled.div`
  background: ${colors.milkGlass800};
  border-radius: ${(p) => p.$r || "4px"};
  height: ${(p) => p.$h || "14px"};
  width: ${(p) => p.$w || "100%"};
`;

const Wrap = SkeletonPulse;
const Bar = SkeletonBar;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.milkGlass600};
  border-radius: 16px;
  overflow: hidden;
`;

// Mirrors the group card's dark Group Codes strip.
const CardHeader = styled.div`
  background: ${colors.milkGlass800};
  height: 50px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const TagRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const GroupsSkeleton = ({ cards = 3, withHeader = true }) => (
  <Wrap role="status" aria-label="Loading groups">
    {withHeader && <Bar $w="320px" $h="40px" $r="8px" />}
    <Grid>
      {Array.from({ length: cards }, (_, i) => (
        <Card key={i}>
          <CardHeader />
          <CardBody>
            <TagRow>
              <Bar $w="56px" $h="24px" $r="11px" />
              <Bar $w="90px" $h="20px" />
            </TagRow>
            <Bar $w="80%" $h="20px" />
            <Bar $w="60%" $h="20px" />
            <Bar $w="140px" $h="16px" />
          </CardBody>
        </Card>
      ))}
    </Grid>
  </Wrap>
);
